export function VictoriaEnforcement() {
  const violations = [
    {
      violation: 'Operating without Provincial Registry Number',
      penalty: '$1,000 per day',
      enforcement: 'Automatic platform de-listing',
      severity: 'critical'
    },
    {
      violation: 'Operating without business license',
      penalty: '$500 - $2,000',
      enforcement: 'Stop-work order issued',
      severity: 'high'
    },
    {
      violation: 'Principal residence violation',
      penalty: '$5,000 - $25,000',
      enforcement: 'License revocation',
      severity: 'critical'
    },
    {
      violation: 'Secondary suite violations',
      penalty: '$2,000 - $10,000',
      enforcement: 'Immediate cease operations',
      severity: 'high'
    },
    {
      violation: 'Fire safety non-compliance',
      penalty: '$1,000 - $5,000',
      enforcement: 'License suspension',
      severity: 'high'
    },
    {
      violation: 'Zoning bylaw violations',
      penalty: '$1,000 - $5,000',
      enforcement: 'Stop-work order',
      severity: 'medium'
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="inline-block bg-[#882629]/10 text-[#882629] px-4 py-2 rounded-full text-sm font-medium mb-4">
            2026 Enforcement
          </span>
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Complaint + Registry Audit Enforcement
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Victoria combines complaint-based investigations with Provincial Registry audits. Stop-work orders are issued for violations, and secondary suite violations carry significant penalties.
          </p>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-8 text-center">
            Common Violations &amp; Penalties
          </h3>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#882629] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Violation</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Penalty Range</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Enforcement Action</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Severity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0F0F0]">
                  {violations.map((item, index) => (
                    <tr key={index} className="hover:bg-[#F0F0F0]/50 transition-colors">
                      <td className="px-6 py-4 text-sm text-[#505A5A]">{item.violation}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-[#882629]">{item.penalty}</td>
                      <td className="px-6 py-4 text-sm text-[#505A5A]/70">{item.enforcement}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          item.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          item.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.severity.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-2xl p-8">
            <div className="w-14 h-14 bg-[#882629]/10 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-user-voice-line text-2xl text-[#882629]"></i>
            </div>
            <h3 className="text-xl font-semibold text-[#505A5A] mb-4">Complaint-Based Investigations</h3>
            <ul className="space-y-3 text-[#505A5A]/70">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                <span>Neighbor complaints trigger immediate investigations</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                <span>Bylaw officers conduct site inspections within 48 hours</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                <span>Anonymous reporting available through 311</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                <span>Stop-work orders issued for confirmed violations</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl p-8">
            <div className="w-14 h-14 bg-[#882629]/10 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-database-2-line text-2xl text-[#882629]"></i>
            </div>
            <h3 className="text-xl font-semibold text-[#505A5A] mb-4">Provincial Registry Audits</h3>
            <ul className="space-y-3 text-[#505A5A]/70">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                <span>Regular cross-referencing with BC Provincial Registry</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                <span>Platform listings monitored for registry compliance</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                <span>Automatic de-listing for missing registry numbers</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#882629] mt-1"></i>
                <span>Secondary suite violations heavily scrutinized</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#882629] to-[#6E1F22] rounded-2xl p-8 lg:p-10 text-white shadow-xl">
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <i className="ri-error-warning-line text-6xl"></i>
            </div>
            <div className="flex-grow text-center lg:text-left">
              <h4 className="text-2xl font-bold mb-3">
                Enforcement Style: Complaint + Registry Audit
              </h4>
              <p className="text-lg text-white/90 leading-relaxed">
                Victoria actively investigates neighbor complaints and conducts regular Provincial Registry audits. Stop-work orders are issued immediately for violations, and secondary suite violations can result in fines up to $10,000. Stay compliant to avoid disruption.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
